/**
 * Listening helpers for word practice
 * Handles browser SpeechRecognition and media recording fallbacks
 */

import { LISTENING } from '@config/constants'

export interface BrowserListeningResult {
  transcript: string
  confidence: number
}

type SpeechRecognitionLike = {
  lang: string
  interimResults: boolean
  maxAlternatives: number
  start: () => void
  stop: () => void
  onresult: ((event: RecognitionResultEvent) => void) | null
  onerror: ((event: Event) => void) | null
  onend: (() => void) | null
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

type RecognitionResultEvent = Event & {
  results: ArrayLike<{ [index: number]: { transcript?: string; confidence?: number } }>
}

export function isBrowserSpeechRecognitionAvailable(): boolean {
  if (typeof window === 'undefined') return false
  const recognition =
    (window as { SpeechRecognition?: SpeechRecognitionConstructor }).SpeechRecognition ||
    (window as { webkitSpeechRecognition?: SpeechRecognitionConstructor }).webkitSpeechRecognition
  return Boolean(recognition)
}

export function createBrowserRecognitionSession({
  idleTimeoutMs = LISTENING.DEFAULT_IDLE_TIMEOUT,
  maxDurationMs = LISTENING.DEFAULT_MAX_DURATION,
}: {
  idleTimeoutMs?: number
  maxDurationMs?: number
} = {}) {
  let stopRecognition: () => void = () => {}

  const promise = new Promise<BrowserListeningResult>((resolve, reject) => {
    if (!isBrowserSpeechRecognitionAvailable()) {
      reject(new Error('Browser speech recognition unavailable'))
      return
    }

    const RecognitionClass =
      (window as { SpeechRecognition?: SpeechRecognitionConstructor }).SpeechRecognition ||
      (window as { webkitSpeechRecognition?: SpeechRecognitionConstructor })
        .webkitSpeechRecognition
    if (!RecognitionClass) {
      reject(new Error('Browser speech recognition unavailable'))
      return
    }

    const recognition = new RecognitionClass()
    recognition.lang = 'en-US'
    recognition.interimResults = false
    recognition.maxAlternatives = 1

    let idleTimer: number | null = null
    let hardStopTimer: number | null = null

    const clearTimers = () => {
      if (idleTimer) window.clearTimeout(idleTimer)
      if (hardStopTimer) window.clearTimeout(hardStopTimer)
    }

    stopRecognition = () => {
      try {
        recognition.stop()
      } catch {
        // ignore
      }
    }

    recognition.onresult = (event: RecognitionResultEvent) => {
      clearTimers()
      const firstResult = event.results?.[0]?.[0]
      resolve({
        transcript: firstResult?.transcript || '',
        confidence: firstResult?.confidence ?? 0,
      })
    }

    recognition.onerror = (event: Event) => {
      clearTimers()
      const errorEvent = event as { error?: string }
      reject(new Error(errorEvent.error || 'Speech recognition error'))
    }

    recognition.onend = () => {
      clearTimers()
    }

    idleTimer = window.setTimeout(() => {
      stopRecognition()
      reject(new Error('No speech detected'))
    }, idleTimeoutMs)

    hardStopTimer = window.setTimeout(() => {
      stopRecognition()
      reject(new Error('Listening timed out'))
    }, maxDurationMs)

    try {
      recognition.start()
    } catch (error) {
      clearTimers()
      reject(error instanceof Error ? error : new Error('Unable to start recognition'))
    }
  })

  return { promise, stop: () => stopRecognition() }
}

export async function createAudioRecordingSession({
  maxDurationMs = LISTENING.DEFAULT_MAX_DURATION,
}: {
  maxDurationMs?: number
} = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  const recorder = new MediaRecorder(stream)
  const chunks: BlobPart[] = []

  let stopped = false

  const promise = new Promise<Blob>((resolve, reject) => {
    const handleStop = () => {
      const blob = new Blob(chunks, { type: 'audio/webm' })
      stream.getTracks().forEach(track => track.stop())
      resolve(blob)
    }

    const stopTimer = window.setTimeout(() => {
      recorder.stop()
    }, maxDurationMs)

    recorder.ondataavailable = event => {
      if (event.data.size > 0) {
        chunks.push(event.data)
      }
    }

    recorder.onerror = event => {
      window.clearTimeout(stopTimer)
      stream.getTracks().forEach(track => track.stop())
      reject(event.error || new Error('Recording failed'))
    }

    recorder.onstop = () => {
      window.clearTimeout(stopTimer)
      handleStop()
    }

    recorder.start()
  })

  const stop = () => {
    if (stopped) return
    stopped = true
    try {
      recorder.stop()
    } catch {
      // ignore
    }
  }

  return { promise, stop }
}
